import React from "react";
import {Popover, Tooltip} from "@mui/material";
import type {ComputedOpportunity} from "../briefingLogic";
import {confidenceState, offerFoundationWorklist} from "../briefingLogic";
import {BRIEFING, CONFIDENCE_DIMENSIONS} from "../briefingData";
import {isDimensionParked, useBriefing} from "../briefingState";

type Props = {
    computed: ComputedOpportunity
    anchorEl: HTMLElement | null
    onClose: () => void
};

/**
 * Confidence breakdown: the four dimensions with their own states. The
 * opportunity's figure is the lowest of the four (weakest link, never the
 * average), and the lowest dimension carries its explanation — what is met,
 * what is missing, and what each miss costs. A dimension routed to its owner
 * renders as parked, not as low. When two or more are weak at once, the
 * sequenced foundation worklist is offered instead of four separate trips.
 */
export const ConfidencePopover: React.FC<Props> = ({computed, anchorEl, onClose}) => {
    const {state} = useBriefing();
    const classMeta = BRIEFING.confidence_model.dimensions;
    const weakCount = Object.values(computed.dimensionConfidences).filter(v => v < 80).length;

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            slotProps={{paper: {className: 'db-pop db-confidence-pop'}}}
        >
            <div className="db-pop-title">
                Confidence {confidenceState(computed.confidence).toLowerCase()} — set by the weakest dimension
            </div>
            {CONFIDENCE_DIMENSIONS.map(cls => {
                const meta = classMeta.find(c => c.id === cls);
                const value = computed.dimensionConfidences[cls];
                const isLowest = value === computed.confidence;
                const parked = isDimensionParked(state, computed.opportunity.id, cls);
                const deductions = computed.checklists[cls].filter(c => c.deduction > 0);
                return (
                    <div key={cls} className={"db-dim-row" + (isLowest && !parked ? " lowest" : "")}>
                        <span className="db-dim-label">
                            {meta?.label ?? cls}
                            <span className="db-dim-question">{meta?.question}</span>
                        </span>
                        <span className="db-dim-value">
                            {parked
                                ? <span className="db-fact-parked">Parked — waiting on an answer</span>
                                : <>
                                    {confidenceState(value)} · {value}%
                                    {isLowest && <span className="db-min-tag">sets the opportunity</span>}
                                </>}
                        </span>
                        {isLowest && !parked && deductions.length > 0 &&
                            <span className="db-checklist">
                                {deductions.map(c =>
                                    <span key={c.id} className="db-check-row">
                                        <span className="db-check-points">−{c.deduction}</span>
                                        {c.label}{c.note ? ` — ${c.note}` : ''}
                                    </span>
                                )}
                            </span>
                        }
                    </div>
                );
            })}
            {offerFoundationWorklist(computed) &&
                <div className="db-worklist-offer">
                    {weakCount} dimensions need confirmation — one sequenced batch beats {weakCount} separate trips.
                    <Tooltip title="The foundation worklist page is not part of the showcase build">
                        <span className="db-fact-unavailable">Open foundation worklist</span>
                    </Tooltip>
                </div>
            }
            <div className="db-pop-note">
                Below {BRIEFING.ready_to_act_gate.confidence_threshold} an opportunity is not ready to act.
            </div>
        </Popover>
    );
};
